import React, { useEffect, useRef, useState } from 'react';
import { Sprout, Zap, TrendingUp, Award } from 'lucide-react';
import './GrowthPath.css';

const pathStages = [
  {
    icon: Sprout,
    title: 'Foundations of Success',
    description: 'Build strong roots and skills that last a lifetime.',
  },
  {
    icon: Zap,
    title: 'Skill Acceleration',
    description: 'Grow confidence and real-world experience.',
  },
  {
    icon: TrendingUp,
    title: 'Career Momentum',
    description: 'Elevate your impact and influence.',
  },
  {
    icon: Award,
    title: 'Mastery & Innovation',
    description: 'Lead, mentor, and innovate in your field.',
  },
];

export function GrowthPath() {
  const [visibleCards, setVisibleCards] = useState<number[]>([]);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = cardsRef.current.indexOf(entry.target as HTMLDivElement);
            if (index !== -1 && !visibleCards.includes(index)) {
              setVisibleCards((prev) => [...prev, index]);
            }
          }
        });
      },
      { threshold: 0.2 }
    );

    cardsRef.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => {
      cardsRef.current.forEach((card) => {
        if (card) observer.unobserve(card);
      });
    };
  }, [visibleCards]);

  return (
    <section className="growth-path section">
      <div className="growth-header">
        <h2 className="section-title">Your Path to Excellence</h2>
        <p className="section-subtitle">
          Every professional has a unique journey. Navigate yours with programs designed to support growth at every milestone.
        </p>
      </div>

      <div className="growth-cards">
        {pathStages.map((stage, index) => {
          const Icon = stage.icon;
          return (
            <div
              key={index}
              ref={(el) => (cardsRef.current[index] = el)}
              className={`growth-card ${visibleCards.includes(index) ? 'visible' : ''}`}
              style={{ animationDelay: `${index * 0.15}s` }}
            >
              <div className="card-icon">
                <Icon size={32} />
              </div>
              <h3 className="card-title">{stage.title}</h3>
              <p className="card-description">{stage.description}</p>
              <button className="card-button">Learn More</button>
            </div>
          );
        })}
      </div>
    </section>
  );
}
